import { useEffect, useState } from 'react';

/**
 * Birden fazla bileşenin (header, sidebar, sayfa) aynı sayacı paylaşması için
 * modül seviyesinde küçük bir store. İlk abone geldiğinde veriyi çeker, belirli
 * aralıklarla ve pencere odağa geldiğinde yeniler; tek bir interval kullanılır.
 */
export function createPollingStore<T>(fetcher: () => Promise<T>, initialValue: T, intervalMs = 60000) {
  let value = initialValue;
  const listeners = new Set<(v: T) => void>();
  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight: Promise<void> | null = null;

  const set = (next: T) => {
    value = next;
    listeners.forEach((listener) => listener(next));
  };

  const refresh = (): Promise<void> => {
    if (inFlight) return inFlight;
    inFlight = fetcher()
      .then(set)
      .catch(() => {
        // Sayaç hataları sessizce yutulur (header/sidebar'da hata mesajı gösterilmez)
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  const handleFocus = () => {
    refresh();
  };

  const subscribe = (listener: (v: T) => void) => {
    listeners.add(listener);
    if (listeners.size === 1) {
      refresh();
      timer = setInterval(refresh, intervalMs);
      window.addEventListener('focus', handleFocus);
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        if (timer) clearInterval(timer);
        timer = null;
        window.removeEventListener('focus', handleFocus);
      }
    };
  };

  const useValue = (enabled = true): T => {
    const [current, setCurrent] = useState<T>(value);

    useEffect(() => {
      if (!enabled) return undefined;
      setCurrent(value);
      return subscribe(setCurrent);
    }, [enabled]);

    return enabled ? current : initialValue;
  };

  return { get: () => value, set, refresh, useValue };
}
